// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useState, useEffect, useCallback, useMemo } from 'react';

import {
  type TaskId,
  useEvolveGet,
  type Task,
  type WorkCellTask,
  formatDate,
  useEvolvePatch,
  type EvolveApiBody,
  type RollupEntityAttachmentType,
} from '@evolvemep/api-ts';
import { LoadingOverlay, Stack, Flex, Title, Text } from '@mantine/core';
import { AgGridReact, type AgGridReactProps } from 'ag-grid-react';
import { useParams } from 'react-router';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AttachmentList } from 'components/Mantine/Attachments/AttachmentList';
import { BasePageHeader } from 'components/Mantine/BasePageHeader';
import { CustomPropertiesDetailSection } from 'components/Mantine/CustomProperties/CustomPropertiesDetailSection';
import { DescriptionEditor } from 'components/Mantine/DetailPage/DescriptionEditor';
import { DetailSection } from 'components/Mantine/DetailPage/DetailSection';
import { EditableDetailSection } from 'components/Mantine/DetailPage/EditableDetailSection';
import { SmallDetailItem } from 'components/Mantine/DetailPage/SmallDetailItem';
import { convertSecondsToDuration } from 'components/Mantine/durationHelpers';
import { DurationInput } from 'components/Mantine/DurationInput';
import { EntityStatusBadge } from 'components/Mantine/EntityStatusBadge';
import { MultiUserSelect } from 'components/Mantine/MultiUserSelect';
import { EvolveLink } from 'components/Mantine/Navigation/EvolveLink';
import { ShopNavigate } from 'components/Mantine/Navigation/ShopNavigate';
import type { EntityState } from 'components/Mantine/StatusEditor/types';
import { UserInfoBadge } from 'components/Mantine/UserInfoBadge';
import { AgGridContainer } from 'helpers/ag-grid/AgGridContainer';
import { baseGridDef, compactRowHeight } from 'helpers/ag-grid/baseColumnDef';
import { EditableCell } from 'helpers/ag-grid/EditableCells/EditableCell';
import { NoRowsOverlay } from 'helpers/ag-grid/NoRowsOverlay';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { useTaskStatuses } from 'hooks/typeCacheHooks/useTaskStatuses';
import { getEventsColDef } from 'modules/Shop/Tasks/TaskViewer/columnDefs';
import { TaskControls } from 'modules/Shop/Tasks/TaskViewer/TaskControls';
import { TextAreaWithButtons } from 'modules/Shop/Tasks/TaskViewer/TextAreaWithButtons';
import { WorkCellName } from 'modules/Shop/WorkOrders/WorkOrderDetail/Tasks/EditableTask';
import { WorkCellSelect } from 'modules/Shop/WorkOrders/WorkOrderDetail/Tasks/MoveWorkCell/WorkCellSelect';

// Module-scoped so the array's identity is stable across renders
const excludedRollupSections: RollupEntityAttachmentType[] = ['workOrderBop', 'workOrderItems'];

type Props = {
  task: Task;
  onTaskUpdated: (task: Task) => void;
  refreshTaskAndWorkCellTask: () => Promise<void>;
  taskStatuses: EntityState<'Task'>[];
  workCellTask: WorkCellTask | null | undefined;
  workOrderIsComplete: boolean | undefined;
  workOrderIsBlocked: boolean;
  loading: boolean;
};

const timeStampsGridProps = {
  ...baseGridDef,
  headerHeight: compactRowHeight,
  rowHeight: compactRowHeight,
  domLayout: 'autoHeight',
  defaultColDef: {
    resizable: false,
    suppressMovable: true,
    sortable: false,
    contextMenuItems: [],
    suppressHeaderContextMenu: true,
    suppressHeaderMenuButton: true,
  },
  noRowsOverlayComponent: () => <NoRowsOverlay label="Task has not been started." align="flex-start" />,
} as const satisfies AgGridReactProps;

export const TaskViewerPageWrapped = ({
  task,
  onTaskUpdated,
  workCellTask,
  taskStatuses,
  workOrderIsBlocked,
  workOrderIsComplete,
  refreshTaskAndWorkCellTask,
  loading: loadingParent,
}: Props) => {
  const eventsColDef = useMemo(() => getEventsColDef(taskStatuses), [taskStatuses]);

  const { data: spool, loading: loadingSpool } = useEvolveGet(`shop/task/${task.taskId}/spool`, { lazy: false });

  const { apiCall: patchTask, loading: savingTask } = useEvolvePatch(`shop/task/${task.taskId}`);
  const { apiCall: updateTaskToWorkCell, loading: savingMoveToWorkCell } = useEvolvePatch(`shop/task/moveToWorkCell`);
  const saving = savingTask || savingMoveToWorkCell || loadingParent;

  const editable = isNotNil(workCellTask) && task.taskStatusTypeName !== 'Completed' && !saving;

  const updateTask = useCallback(
    async (body: EvolveApiBody<'PATCH', 'shop/task/:taskId'>) => patchTask(body).then(onTaskUpdated),
    [patchTask, onTaskUpdated],
  );

  const assigneeUsers = useMemo(() => task.assignees.map(({ user }) => user), [task.assignees]);

  return (
    <Stack flex={1} style={{ overflow: 'hidden' }} gap={0}>
      <BasePageHeader
        title={task.taskName}
        parentPage={{
          title: 'Tasks',
          url: '/tasks',
        }}
        topRightComponent={<TaskControls task={task} disabled={workOrderIsBlocked} onTaskUpdated={onTaskUpdated} />}
      />
      <Flex gap="md" flex="1 0 0" pr={8} align="flex-start" style={{ overflowY: 'scroll' }}>
        {/* Large left column */}
        <Stack flex={1}>
          <DescriptionEditor
            title={
              <Flex align="center" gap="xs">
                {isNotNil(workCellTask) && (
                  <Flex align="center" gap="xs" c="dimmed">
                    <Title order={2}>{workCellTask.workOrderItemName}</Title>
                    <EvolveIcon icon="ArrowRight" size="xs" />
                  </Flex>
                )}
                <Title order={2}>{task.taskName}</Title>
              </Flex>
            }
            description={task.taskDescription}
            updateDescription={(taskDescription) => updateTask({ taskDescription })}
            editable={editable}
          />

          <DetailSection title="Attachments" large>
            <AttachmentList
              taskId={task.taskId}
              workOrderItemId={task.workOrderItemId}
              workOrderId={workCellTask?.workOrderId}
              allowUploads="task"
              excludeSections={excludedRollupSections}
              project={workCellTask ?? undefined}
              spool={spool}
              loading={loadingSpool}
            />
          </DetailSection>

          <EditableDetailSection title="Comments" large editable={editable} noContentMessage="No task comments.">
            {(editing, finishEditing) =>
              editing ? (
                <TextAreaWithButtons
                  onSubmit={async (value) => {
                    await updateTask({ comments: value });
                    finishEditing();
                  }}
                  onClose={finishEditing}
                  defaultValue={task.comments ?? ''}
                  maxLength={512}
                />
              ) : (
                !!task.comments && <Text style={{ whiteSpace: 'pre-wrap' }}>{task.comments}</Text>
              )
            }
          </EditableDetailSection>

          <DetailSection title="Activity" large>
            <AgGridContainer>
              {(applyWrap) => (
                <AgGridReact<Task['time']['events'][number]>
                  rowData={task.time.events}
                  columnDefs={eventsColDef}
                  onFirstDataRendered={applyWrap}
                  {...timeStampsGridProps}
                />
              )}
            </AgGridContainer>
          </DetailSection>
        </Stack>

        {/* Thin right column */}
        <Stack miw={300} w={300}>
          <DetailSection title="Status">
            <EntityStatusBadge customStatusEntityName="Task" stateName={task.taskStatusTypeName} size="xxl" fullWidth />
          </DetailSection>

          <DetailSection
            title="Assignees"
            rightSection={
              <EditableCell
                Editor={MultiUserSelect}
                initialValue={assigneeUsers}
                renderIcon="Add"
                onSave={(assignees) =>
                  patchTask({
                    assignees: assignees?.map((a) => a.userId) ?? [],
                  }).then(onTaskUpdated)
                }
                editable={!saving}
              />
            }
          >
            <EditableCell
              Editor={MultiUserSelect}
              initialValue={assigneeUsers}
              renderValue={
                <Stack gap="xs">
                  {assigneeUsers.map((user) => (
                    <UserInfoBadge key={user.userId} user={user} />
                  ))}
                </Stack>
              }
              emptyState={
                <>
                  <EvolveIcon icon="EmptyCircle" size="sm" />
                  Add assignee
                </>
              }
              showEmptyState={task.assignees.length === 0}
              onSave={(assignees) =>
                patchTask({
                  assignees: assignees?.map((a) => a.userId) ?? [],
                }).then(onTaskUpdated)
              }
              editable={editable}
            />
          </DetailSection>

          <CustomPropertiesDetailSection
            disabled={saving}
            onUpdateCustomProperty={(customPropertyId, value) =>
              updateTask({
                customProperties: [{ customPropertyId, value }],
              })
            }
            entity={task}
            customPropertyTypeName="Task"
          />

          <DetailSection title="Labor">
            {task.taskStatusTypeName === 'Completed' && (
              <SmallDetailItem label="Run Time" tooltip="Duration the task was actively running.">
                {convertSecondsToDuration(task.time.totalTime)}
              </SmallDetailItem>
            )}
            <SmallDetailItem
              label="Estimated Time"
              tooltip="Time estimated for the task. Calculated by multiplying the task type's unit time by the task's associated item quantity."
            >
              {task.estimatedTimeInSeconds ? convertSecondsToDuration(task.estimatedTimeInSeconds) : '--'}
            </SmallDetailItem>
            <SmallDetailItem label="Logged Time" tooltip="Time logged in via time tracking.">
              {task.totalTimeEntriesInSeconds ? convertSecondsToDuration(task.totalTimeEntriesInSeconds) : '--'}
            </SmallDetailItem>
            <EditableCell
              label="Adjusted Time"
              labelTooltip="Overrides run time and logged time."
              Editor={DurationInput}
              validate={Number.isNaN}
              editable={!workOrderIsComplete}
              initialValue={task.time.adjustedTimeInSeconds}
              onSave={async (adjustedTimeInSeconds) => updateTask({ adjustedTimeInSeconds })}
              renderValue={
                task.time.adjustedTimeInSeconds ? convertSecondsToDuration(task.time.adjustedTimeInSeconds) : '--'
              }
            />
          </DetailSection>

          <DetailSection title="Properties">
            <SmallDetailItem label="Item Name">{workCellTask?.workOrderItemName}</SmallDetailItem>
            <SmallDetailItem label="Item Qty">{workCellTask?.quantity}</SmallDetailItem>
            <SmallDetailItem label="Task Name">{task.taskName}</SmallDetailItem>
            <EditableCell
              label="Work Cell"
              Editor={WorkCellSelect}
              editorProps={{
                taskIds: [task.taskId],
                facility: isNotNil(workCellTask)
                  ? { facilityId: workCellTask.facilityId, facilityName: workCellTask.facilityName }
                  : undefined,
              }}
              editable={task.taskStatusTypeName !== 'Completed'}
              initialValue={
                isNotNil(task.workCellId)
                  ? { workCellId: task.workCellId, workCellName: task.workCellName ?? '' }
                  : null
              }
              onSave={async (v) => {
                if (!v) return;
                await updateTaskToWorkCell({
                  taskIds: [task.taskId],
                  workCellId: v.workCellId,
                }).finally(refreshTaskAndWorkCellTask);
              }}
              closeOnChange
              renderValue={<WorkCellName task={task} />}
            />
            <SmallDetailItem label="Project">{workCellTask?.projectName}</SmallDetailItem>
            <SmallDetailItem label="Work Order Id">
              {isNotNil(workCellTask) ? (
                <EvolveLink to={`/work-orders/${workCellTask.workOrderId}`} from={task.taskName}>
                  {workCellTask.workOrderIdentifier}
                </EvolveLink>
              ) : (
                '--'
              )}
            </SmallDetailItem>
            <SmallDetailItem label="Work Order Name">{workCellTask?.workOrderName}</SmallDetailItem>
            <SmallDetailItem label="Work Order Need By">
              {formatDate(workCellTask?.needByDate, 'MM/dd/yyyy')}
            </SmallDetailItem>
          </DetailSection>
        </Stack>
      </Flex>
    </Stack>
  );
};

/**
 * Used to verify the selected `Task` both exists and is loaded into memory.
 */
export const TaskViewerPage = () => {
  const { taskId: taskIdRaw } = useParams();
  const taskId = taskIdRaw as TaskId;
  const { states: taskStatusTypes } = useTaskStatuses();
  const {
    data: taskFromNetwork,
    apiCall: refreshTaskApiCall,
    loading: loadingTask,
  } = useEvolveGet(`shop/task/${taskId}`, { lazy: false });
  const [task, setTask] = useState(taskFromNetwork);
  useEffect(() => {
    setTask(taskFromNetwork);
  }, [taskFromNetwork]);

  const {
    data: workCellTask,
    apiCall: refreshWorkCellTaskApiCall,
    loading: loadingWorkCellTask,
  } = useEvolveGet(`shop/task/${taskId}/workCellTask`, {
    lazy: false,
  });

  const {
    data: workOrder,
    loading: loadingWorkOrder,
    apiCall: refreshWorkOrderApiCall,
  } = useEvolveGet(`shop/workOrder/:workOrderId`);

  const refreshWorkOrder = useCallback(() => {
    if (workCellTask) {
      void refreshWorkOrderApiCall({ url: `shop/workOrder/${workCellTask.workOrderId}` });
    }
  }, [refreshWorkOrderApiCall, workCellTask]);

  useEffect(() => refreshWorkOrder(), [refreshWorkOrder]);

  const workOrderIsComplete =
    task?.taskStatusTypeName === 'Completed'
      ? loadingWorkOrder
        ? undefined
        : workOrder?.workOrderStateName === 'Completed'
      : false;

  const onTaskUpdated = useCallback(
    (updatedTask: Task) => {
      setTask(updatedTask);
      if (updatedTask.taskStatusTypeName === 'Completed') {
        refreshWorkOrder();
      }
    },
    [refreshWorkOrder],
  );

  const refreshTaskAndWorkCellTask = useCallback(async () => {
    await Promise.all([refreshTaskApiCall(), refreshWorkCellTaskApiCall()]);
  }, [refreshTaskApiCall, refreshWorkCellTaskApiCall]);

  if ((loadingTask && isNil(task)) || (loadingWorkCellTask && isNil(workCellTask)) || isNil(taskStatusTypes)) {
    return <LoadingOverlay visible />;
  }
  if (isNil(taskFromNetwork)) {
    return <ShopNavigate to="/tasks" />;
  }
  if (isNil(task)) return <LoadingOverlay visible />;
  return (
    <TaskViewerPageWrapped
      task={task}
      onTaskUpdated={onTaskUpdated}
      workCellTask={workCellTask}
      taskStatuses={taskStatusTypes}
      workOrderIsComplete={workOrderIsComplete}
      workOrderIsBlocked={isNotNil(workOrder) ? workOrder.workOrderStateName === 'Blocked' : true}
      refreshTaskAndWorkCellTask={refreshTaskAndWorkCellTask}
      loading={loadingTask || loadingWorkCellTask || loadingWorkOrder}
    />
  );
};
